import { useEffect, useRef, useState } from 'react';
import { FaCheck, FaPen, FaTimes } from 'react-icons/fa';
import { Spinner } from '../Kit/_index';
import { FieldSelect } from '../Form/_Fields/FieldSelect';

const BLOCK = 'LStudio__app_inline';

/*
    🔥 EDICIÓN EN EL LUGAR

    Tocás un valor, se convierte en campo, guardás. Sin abrir un
    formulario ni un modal para cambiar un teléfono.

    ⚠️ Enter GUARDA, Escape CANCELA, PERDER EL FOCO GUARDA

    Guardar al perder el foco es lo que la gente espera —clickeás
    afuera y quedó—, pero es peligroso si el valor está mal. Por eso
    Escape restaura el original ANTES de que el blur haga nada: se
    limpia el valor y recién ahí se sale.

    ⚠️ EL VALOR MOSTRADO NO ES EL EDITADO

    Mientras se guarda se muestra el valor NUEVO, no el viejo: si
    falla, vuelve el original con el error. Mostrar el viejo durante el
    guardado hace pensar que el cambio no entró.

    ⚠️ EL BOTÓN DE LÁPIZ NO ES LA ÚNICA ENTRADA

    El valor entero es clickeable y llega con Tab. Un lápiz de 14px es
    un objetivo chico, sobre todo en pantalla táctil.
*/

export const InlineEdit = ({
    value,
    onSave,
    type = 'text',               // text | number | textarea | select
    options = null,              // para select
    placeholder = 'Sin valor',
    label = null,
    isDisabled = false,
    validate = null,             // (v) => true | 'mensaje de error'
    format = null,               // (v) => texto a mostrar
    size = 'md',
    isRequired = false,
}) => {

    const REF__useRefInput = useRef(null);

    const [useIsEditing, setIsEditing] = useState(false);
    const [useDraft, setDraft] = useState(value ?? '');
    const [useIsSaving, setIsSaving] = useState(false);
    const [useError, setError] = useState(null);

    // 🔥 Si el padre cambia el valor desde afuera, se refleja
    useEffect(() => { if (!useIsEditing) setDraft(value ?? ''); }, [value, useIsEditing]);

    useEffect(() => {
        if (!useIsEditing) return;
        if (type === 'select') return;
        REF__useRefInput.current?.focus();
        REF__useRefInput.current?.select?.();
    }, [useIsEditing]);

    const onStart = () => {
        if (isDisabled) return;
        setError(null);
        setDraft(value ?? '');
        setIsEditing(true);
    };

    const onCancel = () => {
        setDraft(value ?? '');
        setError(null);
        setIsEditing(false);
    };

    const onCommit = async () => {

        const NEXT = type === 'number' ? Number(useDraft) : useDraft;

        // 🔥 Nada cambió: no se molesta al backend
        if (NEXT === value) { setIsEditing(false); return; }

        if (isRequired && (NEXT === '' || NEXT === null)) {
            setError('Este campo no puede quedar vacío.');
            return;
        }

        if (validate) {
            const RESULT = validate(NEXT);
            if (RESULT !== true) { setError(typeof RESULT === 'string' ? RESULT : 'Valor inválido.'); return; }
        }

        setIsSaving(true);
        setError(null);

        try {
            await onSave?.(NEXT);
            setIsEditing(false);
        } catch (err) {
            setError(err?.message ?? 'No pudimos guardar.');
            setDraft(value ?? '');
        } finally {
            setIsSaving(false);
        }
    };

    const onKeyDown = (event) => {

        if (event.key === 'Escape') {
            event.preventDefault();
            /*
                ⚠️ Se cancela ANTES de que el blur pueda guardar: si no,
                   Escape dispararía el blur y el blur guardaría igual.
            */
            onCancel();
            return;
        }

        if (event.key === 'Enter' && (type !== 'textarea' || event.metaKey || event.ctrlKey)) {
            event.preventDefault();
            onCommit();
        }
    };

    // -------------------- 🎯 LECTURA
    if (!useIsEditing) {

        const IS_EMPTY = value === null || value === undefined || value === '';

        const SHOWN = IS_EMPTY
            ? placeholder
            : (format ? format(value) : String(
                options?.find((o) => o.value === value)?.label ?? value,
            ));

        return (
            <span className={BLOCK} data-size={size} data-error={!!useError}>

                {label && <span className={`${BLOCK}__label`}>{label}</span>}

                <button
                    type="button"
                    className={`${BLOCK}__read`}
                    data-empty={IS_EMPTY}
                    disabled={isDisabled}
                    onClick={onStart}
                    aria-label={label ? `Editar ${label}` : 'Editar valor'}
                >

                    <span className={`${BLOCK}__text`}>{SHOWN}</span>

                    {
                        useIsSaving
                            ? <Spinner size={11} isInline />
                            : !isDisabled && <FaPen className={`${BLOCK}__pen`} aria-hidden="true" />
                    }

                </button>

                {useError && <span className={`${BLOCK}__error`}>{useError}</span>}

            </span>
        );
    }

    // -------------------- 🎯 EDICIÓN
    return (
        <span className={BLOCK} data-size={size} data-editing="true" data-error={!!useError}>

            {label && <span className={`${BLOCK}__label`}>{label}</span>}

            <span className={`${BLOCK}__edit`}>

                {
                    type === 'select'
                        ? (
                            <div className={`${BLOCK}__select`}>
                                <FieldSelect isBare value={useDraft}
                                    options={options ?? []} isSearchable={false}
                                    onChange={(e) => setDraft(e.target.value)} />
                            </div>
                        )
                        : type === 'textarea'
                            ? (
                                <textarea ref={REF__useRefInput} className={`${BLOCK}__input`} rows={3}
                                    value={useDraft} onChange={(e) => setDraft(e.target.value)}
                                    onKeyDown={onKeyDown} onBlur={onCommit} />
                            )
                            : (
                                <input ref={REF__useRefInput} type={type} className={`${BLOCK}__input`}
                                    value={useDraft} onChange={(e) => setDraft(e.target.value)}
                                    onKeyDown={onKeyDown} onBlur={onCommit} />
                            )
                }

                {/*
                    ⚠️ onMouseDown y no onClick: el blur del campo dispara
                       ANTES que el click, así que con onClick el botón de
                       cancelar nunca llegaría a ejecutarse.
                */}
                <button type="button" className={`${BLOCK}__btn`} data-kind="cancel"
                    onMouseDown={(e) => { e.preventDefault(); onCancel(); }}
                    aria-label="Cancelar" >
                    <FaTimes />
                </button>

                <button type="button" className={`${BLOCK}__btn`} data-kind="save"
                    onMouseDown={(e) => { e.preventDefault(); onCommit(); }}
                    aria-label="Guardar" >
                    <FaCheck />
                </button>

            </span>

            {useError && <span className={`${BLOCK}__error`}>{useError}</span>}

            {
                type === 'textarea' && (
                    <span className={`${BLOCK}__hint`}>Ctrl + Enter para guardar · Escape para cancelar</span>
                )
            }

        </span>
    );
};
